import React, { useState } from 'react';
import { ChevronsDownUp, ChevronsUpDown, Plus, ChevronRight, Lock, ListChecks } from 'lucide-react';

export const WEEK_COLORS = ['#14b8a6', '#f59e0b', '#8b5cf6', '#3b82f6', '#ec4899', '#22c55e', '#f97316', '#06b6d4'];
export const weekColor = (index) => WEEK_COLORS[index % WEEK_COLORS.length];

const isSaved = (a) => a.activity_id && !String(a.activity_id).startsWith('temp_');

// Status dot: grey = unsaved activities, amber = drafts, green = all saved & published
export const weekStatus = (period) => {
  const acts = period.activities || [];
  if (acts.some((a) => !isSaved(a)) || period.isNew) return { color: '#71717a', label: 'Has unsaved changes' };
  if (acts.some((a) => (a.status || 'draft') !== 'published')) return { color: '#f59e0b', label: 'Has draft activities' };
  if (acts.length === 0) return { color: '#3f3f46', label: 'No activities' };
  return { color: '#22c55e', label: 'All published' };
};

const periodLabel = (period, unit, i) => period.title || `${unit} ${i + 1}`;

export function WeekNavigator({ periods, groups, colorFor, unit, activeId, onJump, onExpandAll, onCollapseAll, onAdd, onAddModule }) {
  const [collapsedModules, setCollapsedModules] = useState({});
  return (
    <>
      {/* Desktop rail */}
      <aside className="hidden lg:block sticky top-20 self-start max-h-[calc(100vh-6rem)] overflow-y-auto pr-1" data-testid="week-navigator">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[11px] uppercase tracking-[0.14em] text-zinc-500">{unit}s · {periods.length}</p>
          <div className="flex gap-1">
            <button type="button" onClick={onExpandAll} title="Expand all" className="p-1 rounded text-zinc-400 hover:text-white hover:bg-zinc-800" data-testid="expand-all-weeks-btn"><ChevronsUpDown className="h-4 w-4" /></button>
            <button type="button" onClick={onCollapseAll} title="Collapse all" className="p-1 rounded text-zinc-400 hover:text-white hover:bg-zinc-800" data-testid="collapse-all-weeks-btn"><ChevronsDownUp className="h-4 w-4" /></button>
          </div>
        </div>
        <nav className="space-y-1">
          {groups.map((g) => {
            const collapsed = g.module && collapsedModules[g.key];
            return (
              <div key={g.key} className={g.module || groups.length > 1 ? 'mb-2' : ''}>
                {(g.module || groups.length > 1) && (
                  <button
                    type="button"
                    onClick={() => setCollapsedModules((c) => ({ ...c, [g.key]: !c[g.key] }))}
                    className="w-full flex items-center gap-2 px-1.5 py-1.5 text-[11px] uppercase tracking-[0.12em] text-zinc-400 hover:text-white"
                    data-testid={`nav-module-${g.key}`}
                  >
                    <ChevronRight className={`h-3.5 w-3.5 transition-transform ${collapsed ? '' : 'rotate-90'}`} />
                    <span className="h-2 w-2 rounded-full" style={{ backgroundColor: g.color }} />
                    <span className="truncate flex-1 text-left">{g.module ? g.module.title : 'No module'}</span>
                    {g.module?.locked && <Lock className="h-3 w-3 text-rose-400 shrink-0" data-testid={`nav-module-locked-${g.module.module_id}`} />}
                    <span className="text-zinc-600 normal-case tracking-normal">{g.periods.length}</span>
                  </button>
                )}
                {!collapsed && g.periods.map(({ period: p, index: i }) => {
                  const active = p.period_id === activeId;
                  const status = weekStatus(p);
                  const color = colorFor(p, i);
                  return (
                    <button
                      key={p.period_id}
                      type="button"
                      onClick={() => onJump(p.period_id)}
                      className={`w-full text-left flex items-center gap-2.5 px-2.5 py-2 rounded-md border-l-[3px] transition-colors ${active ? 'bg-zinc-800/80 text-white' : 'text-zinc-400 hover:bg-zinc-800/40 hover:text-zinc-200'}`}
                      style={{ borderLeftColor: active ? color : 'transparent' }}
                      data-testid={`week-nav-item-${p.period_id}`}
                    >
                      <span className="h-6 w-6 shrink-0 rounded text-[11px] font-semibold flex items-center justify-center text-black" style={{ backgroundColor: color }}>{i + 1}</span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm truncate">{periodLabel(p, unit, i)}</span>
                        <span className="flex items-center gap-1.5 text-[11px] text-zinc-500">{p.activities?.length || 0} activities<QuizTag period={p} /></span>
                      </span>
                      <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: status.color }} title={status.label} />
                    </button>
                  );
                })}
              </div>
            );
          })}
        </nav>
        <button
          type="button"
          onClick={onAdd}
          className="mt-3 w-full flex items-center justify-center gap-1.5 px-2.5 py-2 rounded-md border border-dashed border-zinc-700 text-sm text-zinc-400 hover:text-white hover:border-teal-500/60 transition-colors"
          data-testid="week-nav-add-btn"
        >
          <Plus className="h-4 w-4" /> Add {unit}
        </button>
        {onAddModule && (
          <button
            type="button"
            onClick={onAddModule}
            className="mt-2 w-full flex items-center justify-center gap-1.5 px-2.5 py-2 rounded-md border border-dashed border-zinc-700 text-sm text-zinc-400 hover:text-white hover:border-teal-500/60 transition-colors"
            data-testid="week-nav-add-module-btn"
          >
            <Plus className="h-4 w-4" /> Add Module
          </button>
        )}
      </aside>

      {/* Mobile chips */}
      <div className="lg:hidden sticky top-16 z-30 -mx-4 px-4 py-2 bg-navigate-dark/95 backdrop-blur border-b border-zinc-800 flex items-center gap-2 overflow-x-auto" data-testid="week-navigator-chips">
        {periods.map((p, i) => {
          const active = p.period_id === activeId;
          return (
            <button
              key={p.period_id}
              type="button"
              onClick={() => onJump(p.period_id)}
              className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border transition-colors ${active ? 'text-white bg-zinc-800' : 'text-zinc-400 border-zinc-700'}`}
              style={{ borderColor: active ? colorFor(p, i) : undefined }}
              data-testid={`week-chip-${p.period_id}`}
            >
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: colorFor(p, i) }} />
              {unit} {i + 1}
              <span className="text-zinc-500">· {p.activities?.length || 0}</span>
            </button>
          );
        })}
        <button type="button" onClick={onAdd} className="shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-full text-xs border border-dashed border-teal-500/50 text-teal-300" data-testid="week-chip-add-btn">
          <Plus className="h-3 w-3" /> Add {unit}
        </button>
        {onAddModule && (
          <button type="button" onClick={onAddModule} className="shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-full text-xs border border-dashed border-teal-500/50 text-teal-300" data-testid="week-chip-add-module-btn">
            <Plus className="h-3 w-3" /> Add Module
          </button>
        )}
        <button type="button" onClick={onExpandAll} className="shrink-0 px-2 text-xs text-zinc-400" data-testid="expand-all-weeks-chip">Expand all</button>
        <button type="button" onClick={onCollapseAll} className="shrink-0 px-2 text-xs text-zinc-400" data-testid="collapse-all-weeks-chip">Collapse all</button>
      </div>
    </>
  );
}

export function CollapsedWeekSummary({ period, color }) {
  const acts = period.activities || [];
  if (acts.length === 0) return <p className="text-xs text-zinc-600 px-6 pb-4">No activities yet</p>;
  return (
    <div className="flex flex-wrap gap-1.5 px-6 pb-4" data-testid={`collapsed-summary-${period.period_id}`}>
      {acts.map((a, i) => (
        <span key={a.activity_id || i} className="text-xs text-zinc-300 bg-zinc-800/70 rounded px-2 py-1 max-w-[260px] truncate border-l-2" style={{ borderLeftColor: color }}>
          {a.title || 'Untitled activity'}
        </span>
      ))}
    </div>
  );
}

const QuizTag = ({ period }) => {
  const quizzes = (period.activities || []).filter((a) => a.has_quiz);
  if (!quizzes.length) return null;
  const qs = quizzes.reduce((n, a) => n + (a.quiz_questions || 0), 0);
  return (
    <span className="shrink-0 inline-flex items-center gap-1 rounded px-1 py-px text-[10px] leading-none font-medium bg-amber-400/10 text-amber-300 border border-amber-400/30"
      title={`${quizzes.length} Learning Unit${quizzes.length > 1 ? 's' : ''} with a quiz · ${qs} questions`} data-testid={`week-nav-quiz-${period.period_id}`}>
      <ListChecks className="h-3 w-3" />Quiz
    </span>
  );
};
